import React, {memo, useCallback, useRef, useState, useEffect} from "react";

import Main from "./main";
import ResultCard from "@/pages/AfterMeet/ResultCard";
import SSEComponent from "@/components/SSEComponent";
import {useSnapshot} from "valtio";
import {sseStore} from "@/utils/store/store";
import {Modal} from "antd";


interface Props {
}

/**
 * 主页面组件 - 单栏布局，Notion 风格
 * 表单始终显示，点击整理后向下滚动到结果区域
 * 结果卡片占屏幕超过 1/4 时隐藏浮动底栏，否则显示
 */
const Index: React.FC<Props> = memo(() => {
  const { data, loading } = useSnapshot(sseStore);
  const resultRef = useRef<HTMLDivElement>(null);
  const [actionBarVisible, setActionBarVisible] = useState(true);
  // 用于强制 Main 组件重新挂载，清空所有本地状态
  const [formKey, setFormKey] = useState(0);

  // 是否处于结果阶段（生成中或已生成）
  const showResult = Boolean(data) || loading;

  // 监听滚动，计算结果卡片在视口中占据的高度比例
  useEffect(() => {
    if (!showResult) {
      setActionBarVisible(true);
      return;
    }

    const resultEl = resultRef.current;
    if (!resultEl) return;

    // 找到滚动容器
    const findScrollParent = (el: HTMLElement | null): HTMLElement | null => {
      while (el) {
        const style = getComputedStyle(el);
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
          return el;
        }
        el = el.parentElement;
      }
      return null;
    };

    const scrollContainer = findScrollParent(resultEl) || document.documentElement;

    const checkVisibility = () => {
      const containerRect = scrollContainer.getBoundingClientRect();
      const resultRect = resultEl.getBoundingClientRect();
      const containerHeight = containerRect.height;

      // 计算结果卡片在滚动容器可视区域内的高度
      const visibleTop = Math.max(resultRect.top, containerRect.top);
      const visibleBottom = Math.min(resultRect.bottom, containerRect.bottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);

      // 结果卡片占容器高度超过 1/4 → 隐藏底栏
      setActionBarVisible(visibleHeight < containerHeight * 0.25);
    };

    scrollContainer.addEventListener('scroll', checkVisibility, { passive: true });
    // 初始检查一次
    checkVisibility();

    return () => scrollContainer.removeEventListener('scroll', checkVisibility);
  }, [showResult]);

  // 清空结果，回到表单初始状态
  const handleNewSession = useCallback(() => {
    sseStore.data = '';
    sseStore.params = {};
    sseStore.requestId = '';
    sseStore.trace = '';
    sseStore.error = null;
    // 强制 Main 重新挂载，清空表单本地状态
    setFormKey(k => k + 1);
  }, []);

  // 用相同参数重新提交
  const handleResubmit = useCallback(() => {
    sseStore.data = '';
    sseStore.error = null;
    sseStore.trace = '';
    sseStore.requestId = `req_${Date.now()}`;
  }, []);

  // 表单提交后滚动到结果区域
  const handleSubmit = useCallback(() => {
    setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, []);

  return (
    <>
      <SSEComponent url="/api/chat/sse" />
      <div className="text-black">
        <div className="flex flex-col">
          {/* 表单区域：始终显示 */}
          <Main key={formKey} onSubmit={handleSubmit} showActionBar={actionBarVisible} />

          {/* 结果区域 */}
          {showResult && (
            <div ref={resultRef}>
              <ResultCard historyItem={undefined} />
              {/* 操作栏 - 仅生成完成后显示，胶囊风格 */}
              {data && !loading && (
                <div className="w-full flex justify-center pt-2 pb-6">
                  <div className="flex items-center bg-white rounded-full border border-[#E9E9E7] shadow-sm overflow-hidden">
                    <button
                      onClick={() => {
                        Modal.confirm({
                          title: '重新整理',
                          content: '将用相同参数再次提交，当前结果将被覆盖。',
                          okText: '确认',
                          cancelText: '取消',
                          onOk: handleResubmit,
                        });
                      }}
                      className="flex items-center gap-2 px-6 py-3 text-sm text-[#37352F] hover:bg-[#F7F7F5] transition-colors cursor-pointer"
                    >
                      <svg className="w-4 h-4 text-[#73726E]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.992 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
                      </svg>
                      重新生成
                    </button>
                    <div className="w-px h-5 bg-[#E9E9E7]" />
                    <button
                      onClick={() => {
                        Modal.confirm({
                          title: '新建任务',
                          content: '将丢弃当前所有状态和参数，回到空白表单。',
                          okText: '确认',
                          cancelText: '取消',
                          onOk: handleNewSession,
                        });
                      }}
                      className="flex items-center gap-2 px-6 py-2.5 mx-1 text-sm font-semibold text-white bg-black rounded-full hover:bg-[#333] transition-colors cursor-pointer"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      新建任务
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
});
Index.displayName = "Index";

export default Index;
